// src/pages/volunteering/editForm.js
import React, { useCallback, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Divider, Input } from "antd";
import Layout from "../../components/layout/layout";
import ConfirmModal from "../../components/confirmModal/confirmModal";
import { getFormById, updateForm } from "./api/forms";
import FieldEditor from "./fieldEditor";
import FieldList from "./fieldList";
import FormPreview from "./formPreview";
import "./volunteering.css";

const { TextArea } = Input;

// ─── Reducer ─────────────────────────────────────────────────────────────────
const initialState = {
  formTitle: "",
  formDescription: "",
  formType: "internal",
  externalFormUrl: "",
  eventDate: "",
  eventVenue: "",
  eventTime: "",
  fields: [],
  isTemplate: false,
  // FIX #4: Track createdAt so it is preserved on update
  createdAt: null,
  isLoading: true,
  isSaving: false,
  editorOpen: false,
  editFieldIndex: null,
  showConfirmModal: false,
  notification: { show: false, message: "", type: "success" }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "setField":
      return { ...state, [action.key]: action.value };
    case "setFields":
      return { ...state, fields: action.payload };
    case "setEditorOpen":
      return { ...state, editorOpen: action.payload };
    case "setEditFieldIndex":
      return { ...state, editFieldIndex: action.payload };
    case "setIsLoading":
      return { ...state, isLoading: action.payload };
    case "setIsSaving":
      return { ...state, isSaving: action.payload };
    case "setConfirmModal":
      return { ...state, showConfirmModal: action.payload };
    case "setNotification":
      return { ...state, notification: action.payload };
    case "loadForm":
      return {
        ...state,
        formTitle: action.payload.title || "",
        formDescription: action.payload.description || "",
        formType: action.payload.formType || "internal",
        externalFormUrl: action.payload.externalFormUrl || "",
        eventDate: action.payload.eventDate || "",
        eventVenue: action.payload.eventVenue || "",
        eventTime: action.payload.eventTime || "",
        fields: action.payload.formType === "external" ? [] : action.payload.formSchema?.fields || [],
        isTemplate: action.payload.isTemplate || false,
        // FIX #4: Store createdAt from the loaded form
        createdAt: action.payload.createdAt || null,
        isLoading: false
      };
    default:
      return state;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
function EditForm() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    formTitle,
    formDescription,
    formType,
    externalFormUrl,
    eventDate,
    eventVenue,
    eventTime,
    fields,
    isTemplate,
    createdAt,
    isLoading,
    isSaving,
    editorOpen,
    editFieldIndex,
    showConfirmModal,
    notification
  } = state;

  // ── Notification helpers ──────────────────────────────────────────────────
  const showNotification = useCallback((message, type = "success") => {
    dispatch({
      type: "setNotification",
      payload: { show: false, message, type }
    });
    setTimeout(() => {
      dispatch({
        type: "setNotification",
        payload: { show: true, message, type }
      });
    }, 0);
  }, []);

  const hideNotification = useCallback(() => {
    dispatch({
      type: "setNotification",
      payload: { ...state.notification, show: false }
    });
  }, [state.notification]);

  // ── Load form ─────────────────────────────────────────────────────────────
  const loadForm = useCallback(async () => {
    dispatch({ type: "setIsLoading", payload: true });
    try {
      const data = await getFormById(formId);
      dispatch({ type: "loadForm", payload: data });
    } catch (err) {
      console.error(err);
      showNotification("Failed to load form", "error");
      dispatch({ type: "setIsLoading", payload: false });
    }
  }, [formId, showNotification]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    if (!formTitle.trim()) {
      showNotification("Form title is required", "error");
      return false;
    }
    if (!eventDate || !eventVenue || !eventTime) {
      showNotification("Event date, venue, and time are all required", "error");
      return false;
    }
    if (formType === "internal" && fields.length === 0) {
      showNotification("Please add at least one field", "error");
      return false;
    }
    if (formType === "external" && !externalFormUrl.trim()) {
      showNotification("Please provide an external form URL", "error");
      return false;
    }
    return true;
  }, [formTitle, eventDate, eventVenue, eventTime, formType, fields, externalFormUrl, showNotification]);

  // ── Update ────────────────────────────────────────────────────────────────
  const handleUpdateForm = useCallback(async () => {
    if (!validate()) return;
    dispatch({ type: "setIsSaving", payload: true });

    try {
      await updateForm({
        formId,
        title: formTitle,
        description: formDescription,
        formType,
        eventDate,
        eventVenue,
        eventTime,
        formSchema: { fields: formType === "internal" ? fields : [] },
        externalFormUrl: formType === "external" ? externalFormUrl : null,
        isTemplate,
        templateName: isTemplate ? formTitle : null,
        // FIX #4: Pass createdAt so the backend can use it as a fallback,
        // though the backend now fetches it directly from DB as the source of truth.
        createdAt
      });

      showNotification(isTemplate ? "Template updated successfully" : "Form updated successfully", "success");
      setTimeout(() => navigate("/volunteering/formList"), 1500);
    } catch (err) {
      console.error(err);
      showNotification("Failed to update", "error");
    } finally {
      dispatch({ type: "setIsSaving", payload: false });
      dispatch({ type: "setConfirmModal", payload: false });
    }
  }, [
    validate,
    formId,
    formTitle,
    formDescription,
    formType,
    eventDate,
    eventVenue,
    eventTime,
    fields,
    externalFormUrl,
    isTemplate,
    createdAt,
    showNotification,
    navigate
  ]);

  // ─── Render ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Layout navTitle="Volunteer Forms">
        <div className="vfl-form-page">
          <Card loading className="vfl-form-card" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      navTitle="Volunteer Forms"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="vfl-form-page">
        <h2 className="vfl-form-page-title">{isTemplate ? "Edit Template" : "Edit Volunteer Form"}</h2>

        {/* ── Form Details ── */}
        <Card className="vfl-form-card">
          <div className="vfl-form-fields">
            <div className="vfl-form-group">
              <label className="vfl-form-label">Form Title *</label>
              <Input
                placeholder="Enter form title"
                value={formTitle}
                onChange={(e) =>
                  dispatch({
                    type: "setField",
                    key: "formTitle",
                    value: e.target.value
                  })
                }
              />
            </div>

            <div className="vfl-form-group">
              <label className="vfl-form-label">Description</label>
              <TextArea
                placeholder="Enter description"
                rows={3}
                value={formDescription}
                onChange={(e) =>
                  dispatch({
                    type: "setField",
                    key: "formDescription",
                    value: e.target.value
                  })
                }
              />
            </div>

            <div className="vfl-form-row">
              <div className="vfl-form-group">
                <label className="vfl-form-label">Event Date *</label>
                <Input
                  type="date"
                  value={eventDate}
                  onChange={(e) =>
                    dispatch({
                      type: "setField",
                      key: "eventDate",
                      value: e.target.value
                    })
                  }
                />
              </div>
              <div className="vfl-form-group">
                <label className="vfl-form-label">Event Time *</label>
                <Input
                  type="time"
                  value={eventTime}
                  onChange={(e) =>
                    dispatch({
                      type: "setField",
                      key: "eventTime",
                      value: e.target.value
                    })
                  }
                />
              </div>
            </div>

            <div className="vfl-form-group">
              <label className="vfl-form-label">Event Venue *</label>
              <Input
                placeholder="Enter event venue"
                value={eventVenue}
                onChange={(e) =>
                  dispatch({
                    type: "setField",
                    key: "eventVenue",
                    value: e.target.value
                  })
                }
              />
            </div>

            {formType === "external" && (
              <div className="vfl-form-group">
                <label className="vfl-form-label">External Form URL *</label>
                <Input
                  placeholder="https://..."
                  value={externalFormUrl}
                  onChange={(e) =>
                    dispatch({
                      type: "setField",
                      key: "externalFormUrl",
                      value: e.target.value
                    })
                  }
                />
              </div>
            )}

            {formType === "internal" && (
              <Button
                onClick={() => {
                  dispatch({ type: "setEditFieldIndex", payload: null });
                  dispatch({ type: "setEditorOpen", payload: true });
                }}
              >
                + Add New Field
              </Button>
            )}
          </div>
        </Card>

        {/* ── Fields List — internal only ── */}
        {formType === "internal" && (
          <>
            <Card className="vfl-form-card">
              <h3 className="vfl-form-section-title">Fields</h3>
              <Divider style={{ margin: "12px 0" }} />
              <FieldList
                fields={fields}
                setFields={(f) => dispatch({ type: "setFields", payload: f })}
                setEditFieldIndex={(i) => dispatch({ type: "setEditFieldIndex", payload: i })}
                setEditorOpen={(v) => dispatch({ type: "setEditorOpen", payload: v })}
              />
            </Card>

            <Card className="vfl-form-card">
              <h3 className="vfl-form-section-title">Preview</h3>
              <Divider style={{ margin: "12px 0" }} />
              <FormPreview fields={fields} />
            </Card>
          </>
        )}

        {/* ── Save ── */}
        <div className="vfl-form-actions">
          <Button className="vfl-form-cancel-btn" onClick={() => navigate("/volunteering/formList")} disabled={isSaving}>
            Cancel
          </Button>
          <Button className="vfl-form-save-btn" loading={isSaving} onClick={() => dispatch({ type: "setConfirmModal", payload: true })}>
            {isTemplate ? "Update Template" : "Update Form"}
          </Button>
        </div>
      </div>

      {/* ── Confirm Update Modal ── */}
      <ConfirmModal
        open={showConfirmModal}
        action="approve"
        entity={isTemplate ? "template" : "form"}
        customTitle={isTemplate ? "Update Template" : "Update Form"}
        customMessage={
          isTemplate ? "Are you sure you want to save changes to this template?" : "Are you sure you want to save these changes?"
        }
        customButtonText="Update"
        loading={isSaving}
        onConfirm={handleUpdateForm}
        onCancel={() => dispatch({ type: "setConfirmModal", payload: false })}
      />

      {/* ── Field Editor Modal ── */}
      {editorOpen && (
        <FieldEditor
          close={() => dispatch({ type: "setEditorOpen", payload: false })}
          fields={fields}
          setFields={(f) => dispatch({ type: "setFields", payload: f })}
          editIndex={editFieldIndex}
        />
      )}
    </Layout>
  );
}

export default EditForm;
